import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './contexts/AuthContext';
import { config } from './config';
import Login from './components/Login';
import Welcome from './components/Welcome';
import Tutorial from './components/Tutorial';
import Onboarding from './components/Onboarding';
import Candidates from './components/Candidates';
import BillFollowButton from './components/BillFollowButton';
import MySubscriptions from './components/MySubscriptions';
import FeedbackForm from './components/FeedbackForm';
import './App.css';

type Bill = {
  id: string;
  title: string;
  summary: string;
  url: string;
  upvotes: number;
  downvotes: number;
  user_vote?: 'upvote' | 'downvote' | null;
  keywords?: string[];
  status?: string;
  published?: string;
  sponsor?: string;
};

type Comment = {
  id: string;
  text: string;
  timestamp: string;
};

const App: React.FC = () => {
  const { currentUser, jwtToken, logout } = useAuth();
  const [bills, setBills] = useState<Bill[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string |null>(null);
  const [comments, setComments] = useState<{ [billId: string]: Comment[] }>({});
  const [newComments, setNewComments] = useState<{ [billId: string]: string }>({});
  const [lastUpdate, setLastUpdate] = useState<string |null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<string[]>([]);
  const [hasSeenWelcome, setHasSeenWelcome] = useState<boolean>(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState<boolean>(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useState<boolean>(false);
  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [showSubscriptions, setShowSubscriptions] = useState<boolean>(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState<boolean>(false);
  const [userSubscriptions, setUserSubscriptions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'bills' | 'candidates'>('bills');
  const [hideCommentsVotes, setHideCommentsVotes] = useState<boolean>(() => {
    return localStorage.getItem('hideCommentsVotes') === 'true';
  });
  
  // Check if this is a first-time user (no previous login)
  const isFirstTimeUser = !localStorage.getItem('hasLoggedInBefore');

  // Initialize localStorage flags on component mount
  useEffect(() => {
    const hasSeenWelcomeFlag = localStorage.getItem('hasSeenWelcome') === 'true';
    const hasSeenOnboardingFlag = localStorage.getItem('hasSeenOnboarding') === 'true';
    const hasSeenTutorialFlag = localStorage.getItem('hasSeenTutorial') === 'true';
    
    setHasSeenWelcome(hasSeenWelcomeFlag);
    setHasSeenOnboarding(hasSeenOnboardingFlag);
    setHasSeenTutorial(hasSeenTutorialFlag);
  }, []);

  // Generate autocomplete suggestions based on available keywords
  useEffect(() => {
    if (bills.length > 0 && searchTerm.trim()) {
      const allKeywords = new Set<string>();
      bills.forEach(bill => {
        if (bill.keywords) {
          bill.keywords.forEach(keyword => allKeywords.add(keyword));
        }
      });
      
      const suggestions = Array.from(allKeywords)
        .filter(keyword => 
          keyword.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !appliedFilters.includes(keyword)
        )
        .slice(0, 5);
      
      setAutocompleteSuggestions(suggestions);
      setShowAutocomplete(suggestions.length > 0);
    } else {
      setShowAutocomplete(false);
      setAutocompleteSuggestions([]);
    }
  }, [searchTerm, bills, appliedFilters]);

  // Fetch user subscriptions when user logs in
  useEffect(() => {
    if (jwtToken) {
      fetchUserSubscriptions();
    }
  }, [jwtToken]);

  useEffect(() => {
    if (currentUser && jwtToken) {
      console.log('App component mounted, fetching bills...');
      
      // Mark that user has logged in before
      localStorage.setItem('hasLoggedInBefore', 'true');
      
      // First check bill status
      fetch(`${config.API_BASE_URL}/bills/status`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      })
        .then(res => res.json())
        .then(statusData => {
          if (statusData.last_update) {
            setLastUpdate(statusData.last_update);
          }
        })
        .catch(error => {
          console.error('Error fetching bill status:', error);
        });
      
      // Then fetch bills
      fetch(`${config.API_BASE_URL}/bills`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      })
        .then(res => {
          console.log('Response received:', res);
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then(async (data) => {
          console.log('Bills data received:', data);
          
          // Show bills immediately as they're fetched
          if (data.bills && data.bills.length > 0) {
            setBills(data.bills);
            setIsInitializing(false);
            
            // Load comments progressively for each bill
            data.bills.forEach(async (bill: Bill) => {
              try {
                const response = await fetch(`${config.API_BASE_URL}/bills/${bill.id}/comments`, {
                  headers: {
                    'Authorization': `Bearer ${jwtToken}`,
                    'Content-Type': 'application/json',
                  },
                });
                if (response.ok) {
                  const commentData = await response.json();
                  setComments(prev => ({
                    ...prev,
                    [bill.id]: commentData.comments
                  }));
                }
              } catch (error) {
                console.error(`Error loading comments for bill ${bill.id}:`, error);
                // Set empty comments array for failed loads
                setComments(prev => ({
                  ...prev,
                  [bill.id]: []
                }));
              }
            });
          } else {
            setBills([]);
            setIsInitializing(false);
          }
        })
        .catch(error => {
          console.error('Error fetching bills:', error);
          setError(error.message);
          setIsInitializing(false);
        });
    }
  }, [currentUser, jwtToken]);

  const handleVote = async (billId: string, voteType: 'upvote' | 'downvote') => {
    if (!jwtToken) return;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/bills/${billId}/vote`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: voteType }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setBills(prevBills => 
          prevBills.map(bill => 
            bill.id === billId 
              ? { 
                  ...bill, 
                  upvotes: result.upvotes, 
                  downvotes: result.downvotes,
                  user_vote: result.user_vote
                }
              : bill
          )
        );
      }
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handleAddComment = async (billId: string) => {
    if (!jwtToken) return;
    
    const commentText = newComments[billId];
    if (!commentText?.trim()) return;

    try {
      const response = await fetch(`${config.API_BASE_URL}/bills/${billId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          text: commentText,
          timestamp: new Date().toISOString()
        }),
      });
      
      if (response.ok) {
        // Clear the input
        setNewComments(prev => ({ ...prev, [billId]: '' }));
        // Reload comments
        try {
          const commentResponse = await fetch(`${config.API_BASE_URL}/bills/${billId}/comments`, {
            headers: {
              'Authorization': `Bearer ${jwtToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (commentResponse.ok) {
            const commentData = await commentResponse.json();
            setComments(prev => ({ ...prev, [billId]: commentData.comments }));
          }
        } catch (error) {
          console.error('Error reloading comments:', error);
        }
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleCommentChange = (billId: string, value: string) => {
    setNewComments(prev => ({ ...prev, [billId]: value }));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleCommentsVotes = () => {
    const newValue = !hideCommentsVotes;
    setHideCommentsVotes(newValue);
    localStorage.setItem('hideCommentsVotes', String(newValue));
  };

  const handleGetStarted = () => {
    // Don't set the flag here - only set it when they actually log in
    setHasSeenWelcome(true);
  };

  const handleOnboardingComplete = async (preferences?: any) => {
    setHasSeenOnboarding(true);
    localStorage.setItem('hasSeenOnboarding', 'true');
    // Mark user as no longer first-time
    localStorage.setItem('hasLoggedInBefore', 'true');
    
    // Send preferences to backend if available
    if (preferences && jwtToken) {
      try {
        const response = await fetch(`${config.API_BASE_URL}/user/preferences`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(preferences),
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('Preferences saved successfully:', result);
          
          // Show success message if email notifications were enabled
          if (result.email_notifications_enabled) {
            console.log('Email notifications enabled - welcome email should be sent');
          }
        } else {
          console.error('Failed to save preferences:', response.statusText);
        }
      } catch (error) {
        console.error('Error saving preferences:', error);
      }
    }
  };

  const handleTutorialComplete = () => {
    setHasSeenTutorial(true);
    setShowTutorial(false);
  };

  const handleShowTutorial = () => {
    setShowTutorial(true);
  };

  // Function to determine bill status based on bill ID and other indicators
  const getBillStatus = (billId: string, title: string, summary: string): { status: string; color: string; icon: string } => {
    const text = `${title} ${summary}`.toLowerCase();
    
    // Check for specific status indicators in the text
    if (text.includes('enacted') || text.includes('passed') || text.includes('signed into law')) {
      return { status: 'Enacted', color: '#28a745', icon: '✅' };
    }
    if (text.includes('vetoed') || text.includes('presidential veto')) {
      return { status: 'Vetoed', color: '#dc3545', icon: '❌' };
    }
    if (text.includes('conference') || text.includes('reconciliation')) {
      return { status: 'In Conference', color: '#ffc107', icon: '🤝' };
    }
    if (text.includes('senate') && text.includes('house')) {
      return { status: 'Both Chambers', color: '#17a2b8', icon: '🏛️' };
    }
    if (text.includes('senate')) {
      return { status: 'In Senate', color: '#6f42c1', icon: '🏛️' };
    }
    if (text.includes('house')) {
      return { status: 'In House', color: '#007bff', icon: '🏛️' };
    }
    if (text.includes('committee')) {
      return { status: 'In Committee', color: '#fd7e14', icon: '📋' };
    }
    if (text.includes('introduced')) {
      return { status: 'Introduced', color: '#6c757d', icon: '📝' };
    }
    
    // Default to "Introduced" for new bills without specific status
    return { status: 'Introduced', color: '#6c757d', icon: '📝' };
  };

  // Function to get keywords from bill data (now stored in backend)
  const getBillKeywords = (bill: Bill): string[] => {
    return bill.keywords || [];
  };

  // Add a filter
  const addFilter = (filter: string) => {
    if (!appliedFilters.includes(filter)) {
      const newFilters = [...appliedFilters, filter];
      setAppliedFilters(newFilters);
      setSearchTerm('');
      setShowAutocomplete(false);
      // Trigger search immediately
      performSearch(newFilters, '');
    }
  };

  // Remove a filter
  const removeFilter = (filter: string) => {
    const newFilters = appliedFilters.filter(f => f !== filter);
    setAppliedFilters(newFilters);
    
    // Trigger search immediately
    if (newFilters.length > 0) {
      performSearch(newFilters, searchTerm);
    } else {
      // If no filters left, fetch all bills
      fetchAllBills();
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setAppliedFilters([]);
    fetchAllBills();
  };

  // Fetch user subscriptions
  const fetchUserSubscriptions = async () => {
    if (!jwtToken) return;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/user/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const subscriptionIds = data.subscriptions.map((sub: any) => sub.bill_id);
        setUserSubscriptions(subscriptionIds);
      }
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
    }
  };

  // Fetch all bills
  const fetchAllBills = async () => {
    if (!jwtToken) return;
    
    setSearchLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/bills`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Sort bills by published date (most recent first)
      const sortedBills = [...data.bills].sort((a: Bill, b: Bill) => {
        const dateA = a.published ? new Date(a.published).getTime() : 0;
        const dateB = b.published ? new Date(b.published).getTime() : 0;
        return dateB - dateA;
      });
      setBills(sortedBills);
      loadCommentsForBills(sortedBills);
    } catch (error) {
      console.error('Error fetching all bills:', error);
      setError('Error fetching bills');
    } finally {
      setSearchLoading(false);
    }
  };

  // Perform search with filters and search term
  const performSearch = async (filters: string[], searchText: string) => {
    if (!jwtToken) return;
    
    setSearchLoading(true);
    try {
      // Fetch all bills first
      const response = await fetch(`${config.API_BASE_URL}/bills`, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Combine search term and applied filters
      const allSearchTerms = [...filters];
      if (searchText.trim()) {
        allSearchTerms.push(searchText.trim());
      }
      
      if (allSearchTerms.length === 0) {
        setBills(data.bills);
        loadCommentsForBills(data.bills);
        return;
      }
      
      // Filter bills based on search terms
      const filteredBills = data.bills
        .map((bill: Bill) => {
          const billKeywords = getBillKeywords(bill);
          let relevanceScore = 0;
          
          allSearchTerms.forEach(searchLower => {
            const searchLowerCase = searchLower.toLowerCase();
            
            // Check keyword matches
            const keywordMatch = billKeywords.some(keyword => 
              keyword.toLowerCase().includes(searchLowerCase) || 
              searchLowerCase.includes(keyword.toLowerCase())
            );
            if (keywordMatch) relevanceScore += 10;
            
            // Check title match
            const titleMatch = bill.title.toLowerCase().includes(searchLowerCase);
            if (titleMatch) relevanceScore += 5;
            
            // Check summary match
            const summaryMatch = bill.summary.toLowerCase().includes(searchLowerCase);
            if (summaryMatch) relevanceScore += 3;
            
            // Check exact keyword matches
            const exactKeywordMatches = billKeywords.filter(keyword => 
              keyword.toLowerCase() === searchLowerCase
            ).length;
            relevanceScore += exactKeywordMatches * 2;
          });
          
          return { ...bill, relevanceScore };
        })
        .filter((bill: Bill & { relevanceScore: number }) => bill.relevanceScore > 0)
        .sort((a: Bill & { relevanceScore: number }, b: Bill & { relevanceScore: number }) => {
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          const aEngagement = a.upvotes + a.downvotes;
          const bEngagement = b.upvotes + b.downvotes;
          return bEngagement - aEngagement;
        })
        .map(({ relevanceScore, ...bill }: Bill & { relevanceScore: number }) => bill);
      
      setBills(filteredBills);
      loadCommentsForBills(filteredBills);
      
    } catch (error) {
      console.error('Error searching bills:', error);
      setError('Error searching bills');
    } finally {
      setSearchLoading(false);
    }
  };

  const loadCommentsForBills = useCallback(async (billsToLoad: Bill[]) => {
    if (!jwtToken) return;
    
    // Load comments progressively for each bill
    billsToLoad.forEach(async (bill: Bill) => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/bills/${bill.id}/comments`, {
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const commentData = await response.json();
          setComments(prev => ({
            ...prev,
            [bill.id]: commentData.comments
          }));
        }
      } catch (error) {
        console.error(`Error loading comments for bill ${bill.id}:`, error);
        // Set empty comments array for failed loads
        setComments(prev => ({
          ...prev,
          [bill.id]: []
        }));
      }
    });
  }, [jwtToken]);

  // Check if backend is available
  const checkBackendHealth = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/bills/status`);
      return response.ok;
    } catch (error) {
      console.error('Backend health check failed:', error);
      return false;
    }
  };

  // Show welcome page for users who are not logged in and haven't clicked get started
  if (!currentUser && !hasSeenWelcome) {
    return <Welcome onGetStarted={handleGetStarted} />;
  }

  // Show login page for users who are not logged in
  if (!currentUser) {
    return <Login defaultToSignup={isFirstTimeUser} />;
  }

  // Show onboarding questionnaire only for first-time users after initial signup
  // Show onboarding for users who haven't seen it or clicked the survey button
  if (currentUser && !hasSeenOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  // Show tutorial page for logged-in users who haven't seen it yet (first time only)
  if (currentUser && !hasSeenTutorial) {
    return <Tutorial onContinue={handleTutorialComplete} />;
  }

  // Show tutorial when info button is clicked
  if (currentUser && showTutorial) {
    return <Tutorial onContinue={handleTutorialComplete} />;
  }

  // No blocking loading screens - bills will appear as they load

  if (error) {
    return <div className="App"><h1>Error: {error}</h1></div>;
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-left">
          <h1>Recent Bills</h1>
          {lastUpdate && (
            <div className="last-update">
              Last updated: {new Date(lastUpdate).toLocaleString()}
            </div>
          )}
        </div>
        <div className="user-info">
          <span>Welcome, {currentUser.email}</span>
          <button 
            onClick={() => setShowFeedbackForm(true)}
            className="feedback-btn"
            title="Share your feedback about OpenBill"
          >
            💬 Feedback
          </button>
          <button 
            onClick={() => setShowSubscriptions(true)}
            className="subscriptions-btn"
            title="View your bill subscriptions"
          >
            📋 My Bills
          </button>
          <button 
            onClick={toggleCommentsVotes}
            className={`toggle-view-btn ${hideCommentsVotes ? 'hidden' : 'shown'}`}
            title={hideCommentsVotes ? "Show comments and votes" : "Hide comments and votes"}
          >
            {hideCommentsVotes ? '👁️ Show' : '🙈 Hide'} Comments
          </button>
          <button 
            onClick={() => {
              localStorage.removeItem('hasSeenOnboarding');
              setHasSeenOnboarding(false);
            }}
            className="survey-btn"
            title="Retake the onboarding survey"
          >
            📝 Survey
          </button>
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>
      
      {/* Tab Navigation - Temporarily disabled */}
      {/* <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'bills' ? 'active' : ''}`}
          onClick={() => setActiveTab('bills')}
        >
          📋 Recent Bills
        </button>
        <button
          className={`tab-button ${activeTab === 'candidates' ? 'active' : ''}`}
          onClick={() => setActiveTab('candidates')}
        >
          👥 Candidates
        </button>
      </div> */}
      
      {/* {activeTab === 'candidates' ? (
        <Candidates />
      ) : ( */}
        <>
          <div className="search-section-large">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search by topic (e.g., immigration, health"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && searchTerm.trim()) {
                    addFilter(searchTerm.trim());
                  }
                }}
                className="search-input-large"
                onFocus={() => setShowAutocomplete(true)}
                onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
              />
              {searchTerm && (
                <button
                  className="search-clear-btn"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
              {showAutocomplete && autocompleteSuggestions.length > 0 && (
                <div className="autocomplete-dropdown">
                  {autocompleteSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="autocomplete-item"
                      onClick={() => addFilter(suggestion)}
                    >
                      <span className="autocomplete-icon">🔍</span>
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => {
                if (searchTerm.trim()) {
                  addFilter(searchTerm.trim());
                }
              }} 
              className="search-btn-large"
              disabled={searchLoading}
            >
              🔍 Search Bills
            </button>
            <button
              className="clear-all-btn"
              onClick={clearAllFilters}
              disabled={appliedFilters.length === 0}
              title="Clear all filters"
            >
              🗑️ Clear All
            </button>
            {searchLoading && (
              <div className="search-loading-indicator">
                <span>Searching bills...</span>
              </div>
            )}
          </div>

          {/* Applied Filters */}
          {appliedFilters.length > 0 && (
            <div className="active-filters-container">
              <div className="active-filters-label">ACTIVE FILTERS</div>
              <div className="active-filters-box">
                {appliedFilters.map((filter, index) => (
                  <div key={index} className="filter-tag">
                    <span className="filter-text">{filter}</span>
                    <button
                      className="filter-remove-btn"
                      onClick={() => removeFilter(filter)}
                      title={`Remove ${filter} filter`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Suggested Topics based on user preferences */}
          {(() => {
            const userPrefs = localStorage.getItem('userPreferences');
            if (userPrefs && !searchTerm && appliedFilters.length === 0) {
              try {
                const prefs = JSON.parse(userPrefs);
                const interests = prefs.interests || [];
                if (interests.length > 0) {
                  return (
                    <div className="suggested-topics">
                      <div className="suggested-topics-label">💡 Suggested topics based on your interests:</div>
                      <div className="suggested-topics-list">
                                                 {interests.slice(0, 5).map((interest: string, idx: number) => (
                          <button
                            key={idx}
                            className="suggested-topic-btn"
                            onClick={() => addFilter(interest)}
                          >
                            {interest}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
              } catch (e) {
                console.error('Error parsing user preferences:', e);
              }
            }
            return null;
          })()}
          
          {bills.length === 0 && !isInitializing ? (
            <div className="no-bills-message">
              <p>No bills found.</p>
              {(searchTerm || appliedFilters.length > 0) && <p>Try adjusting your search terms or filters.</p>}
            </div>
          ) : (
            <>
              {isInitializing && bills.length === 0 && (
                <div className="loading-bills-indicator">
                  <div className="loading-spinner"></div>
                  <p>Loading bills...</p>
                </div>
              )}
              <div className="bills-grid">
                {bills.map(bill => (
                  <div key={bill.id} className="bill-card">
                    <div className="bill-header">
                      <div className="bill-title-section">
                        <div className="bill-number">{bill.id.toUpperCase()}</div>
                        <div className="bill-status">
                          {bill.id.startsWith('hr') ? '🏛️ House Bill' : bill.id.startsWith('s') ? '🏛️ Senate Bill' : '📋 Bill'}
                        </div>
                        <h2>{bill.title.replace(/^(H\.R\.|S\.)\s+\d+\s*-\s*/, '')}</h2>
                      </div>
                      <div className="bill-actions">
                        <a 
                          href={bill.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="pdf-link"
                        >
                          📄 View Bill PDF
                        </a>
                      </div>
                    </div>
                    
                    {/* Bill Follow Button */}
                    <BillFollowButton 
                      billId={bill.id} 
                      jwtToken={jwtToken}
                      isSubscribed={userSubscriptions.includes(bill.id)}
                      onSubscriptionChange={(isSubscribed) => {
                        if (isSubscribed) {
                          setUserSubscriptions(prev => [...prev, bill.id]);
                        } else {
                          setUserSubscriptions(prev => prev.filter(id => id !== bill.id));
                        }
                      }}
                    />
                    
                    {/* Bill Status with Introduction Date */}
                    {(() => {
                      const statusInfo = getBillStatus(bill.id, bill.title, bill.summary);
                      return (
                        <div className="bill-status-section">
                          <div className="status-badge" style={{ backgroundColor: statusInfo.color }}>
                            <span className="status-icon">{statusInfo.icon}</span>
                            <span className="status-text">{statusInfo.status}</span>
                          </div>
                          {bill.published && (
                            <div className="intro-date-inline">
                              {new Date(bill.published).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Sponsor Info (if available) */}
                    {bill.sponsor && bill.sponsor !== "Sponsor information not available" && (
                      <div className="introduction-info">
                        <div className="intro-sponsor">
                          <strong>By:</strong> {bill.sponsor}
                        </div>
                      </div>
                    )}
                    
                    {/* Keywords */}
                    {(() => {
                      const keywords = getBillKeywords(bill);
                      if (keywords.length > 0) {
                        return (
                          <div className="bill-keywords">
                            <div className="keywords-label">
                              🔑 Key Topics
                              <span className="keywords-count">({keywords.length})</span>
                            </div>
                            <div className="keywords-list">
                              {keywords.map((keyword, idx) => (
                                <span 
                                  key={idx} 
                                  className="keyword-tag clickable"
                                  onClick={() => addFilter(keyword)}
                                  title={`Click to add "${keyword}" as a filter`}
                                >
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    <div className="vote-stats">
                      <div className="vote-map-label">Map</div>
                      <div className="vote-ratio-bar">
                        {bill.upvotes + bill.downvotes > 0 ? (
                          <>
                            <div 
                              className="vote-ratio-green" 
                              style={{ 
                                width: `${(bill.upvotes / (bill.upvotes + bill.downvotes)) * 100}%` 
                              }}
                            ></div>
                            <div 
                              className="vote-ratio-red" 
                              style={{ 
                                width: `${(bill.downvotes / (bill.upvotes + bill.downvotes)) * 100}%` 
                              }}
                            ></div>
                          </>
                        ) : (
                          <div 
                            className="vote-ratio-grey" 
                            style={{ width: '100%' }}
                          ></div>
                        )}
                      </div>
                    </div>
                    
                    <div className="bill-summary">
                      <h3>Summary</h3>
                      <ul>
                        {bill.summary.split('\n').filter(line => line.trim() !== '').map((point, idx) => (
                          <li key={idx}>{point.trim()}</li>
                        ))}
                      </ul>
                    </div>
                    
                    {!hideCommentsVotes && (
                      <>
                        <div className="votes">
                          <button 
                            onClick={() => handleVote(bill.id, 'upvote')}
                            className={`vote-button upvote ${bill.user_vote === 'upvote' ? 'active' : ''}`}
                          >
                            👍 {bill.upvotes}
                          </button>
                          <button 
                            onClick={() => handleVote(bill.id, 'downvote')}
                            className={`vote-button downvote ${bill.user_vote === 'downvote' ? 'active' : ''}`}
                          >
                            👎 {bill.downvotes}
                          </button>
                        </div>
                        
                        <div className="comments">
                          <h3>Comments</h3>
                          <div className="comment-list">
                            {comments[bill.id]?.length > 0 ? (
                              comments[bill.id].map((comment, idx) => (
                                <div key={idx} className="comment">
                                  <p>{comment.text}</p>
                                  <small>{new Date(comment.timestamp).toLocaleString()}</small>
                                </div>
                              ))
                            ) : (
                              <p>No comments yet. Be the first to comment!</p>
                            )}
                          </div>
                          <div className="comment-input">
                            <input 
                              type="text" 
                              placeholder="Add a comment..." 
                              value={newComments[bill.id] || ''}
                              onChange={(e) => handleCommentChange(bill.id, e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddComment(bill.id)}
                            />
                            <button onClick={() => handleAddComment(bill.id)}>Post</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      {/* )} */}
      
      {/* Floating Question Mark Button */}
      <button 
        onClick={handleShowTutorial}
        className="floating-help-btn"
        title="How a Bill Becomes a Law"
      >
        ?
      </button>
      
      {/* Subscriptions Modal */}
      {showSubscriptions && (
        <MySubscriptions 
          jwtToken={jwtToken}
          subscriptions={userSubscriptions}
          onClose={() => setShowSubscriptions(false)}
          onSubscriptionChange={(billId: string, isSubscribed: boolean) => {
            if (isSubscribed) {
              setUserSubscriptions(prev => [...prev, billId]);
            } else {
              setUserSubscriptions(prev => prev.filter(id => id !== billId));
            }
          }}
        />
      )}
      
      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <FeedbackForm
          jwtToken={jwtToken}
          onClose={() => setShowFeedbackForm(false)}
        />
      )}
    </div>
  );
};

export default App;
